import React, { useContext } from 'react';
import type { WInFlowThemeComponentAssetSummaryView } from '@zola/svc-web-api-ts-client';
import styled from '@emotion/styled';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';
import {
  mapFontValuesToSerializedStyles,
  mapPseudoElementToCSS,
} from 'components/publicWebsiteV2/util/mappers';
import { ThemeContext } from '../../../context';
import { renderAbsoluteAssets } from '../../../util/renderHelpers';

type NamesModulesProps = {
  ownerFirstName?: string;
  ownerLastName?: string;
  partnerFirstName?: string;
  partnerLastName?: string;
  className?: string;
};

const NamesModule = ({
  ownerFirstName,
  ownerLastName,
  partnerFirstName,
  partnerLastName,
  className,
}: NamesModulesProps): JSX.Element => {
  const {
    components: { globalHeaderFontValues, globalBodyFontValues },
    wedding,
  } = useContext(ThemeContext);

  const namesComponent = wedding?.public_theme_v2?.components?.COUPLES_NAMES;
  const finalHeaderFont = mapFontValuesToSerializedStyles(
    globalHeaderFontValues,
    namesComponent?.fonts?.HEADER?.[1]
  );
  const finalBodyFont = mapFontValuesToSerializedStyles(
    globalBodyFontValues,
    namesComponent?.fonts?.BODY?.[1]
  );

  const { ORIENTATION, SHOW_LAST_NAME, ARTICLE_TEXT } = namesComponent?.option_type_values || {};
  const orientation = ORIENTATION?.value || 'HORIZONTAL';
  const showLastName = Boolean(SHOW_LAST_NAME?.value || 'TRUE');
  const articleText = ARTICLE_TEXT?.value || 'and';

  const { orientation: assetOrientation, before, after } = namesComponent?.in_flow_assets || {};
  const displayStyle = `display: ${
    assetOrientation ===
    (('HORIZONTAL' as unknown) as WInFlowThemeComponentAssetSummaryView.OrientationEnum)
      ? 'inline-block'
      : 'block'
  }`;

  const Container = styled.div`
    padding-top: 90px;
    padding-bottom: 120px;

    ${MEDIA_QUERY.EXCLUDE_DESKTOP} {
      padding-top: 10px;
    }
    display: flex;
    align-items: center;
    justify-content: space-around;
    text-align: center;

    width: 100%;
    ${displayStyle};
    position: relative;

    &:before {
      ${displayStyle};
      margin-left: auto;
      margin-right: auto;
      vertical-align: middle;
      position: relative; // ALLOW OFFSET?
      ${mapPseudoElementToCSS(before)}
    }

    &:after {
      ${displayStyle};
      margin-left: auto;
      margin-right: auto;
      vertical-align: middle;
      ${mapPseudoElementToCSS(after)}
    }
  `;

  // OTOD: CONTENT WIDTH CONSTATNS
  const ContentContainer = styled.div`
    max-width: 800px;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    flex-direction: ${orientation === 'HORIZONTAL' ? 'row' : 'column'};

    ${MEDIA_QUERY.MOBILE} {
      flex-direction: column;
    }
  `;

  const Name = styled.div`
    display: flex;
    flex-direction: ${orientation === 'HORIZONTAL' ? 'column' : 'row'};
    ${orientation === 'HORIZONTAL' && 'width: 30%;'}

    ${MEDIA_QUERY.MOBILE} {
      align-items: center;
    }
  `;

  const OwnerName = styled(Name)`
    align-items: ${orientation === 'HORIZONTAL' ? 'flex-end' : 'center'};
  `;

  const PartnerName = styled(Name)`
    align-items: ${orientation === 'HORIZONTAL' ? 'flex-start' : 'center'};
  `;

  const And = styled.div`
    ${finalBodyFont}
    margin: ${orientation === 'HORIZONTAL' ? '0 32px' : '32px 0'};
    flex-grow: 1;
    ${MEDIA_QUERY.MOBILE} {
      margin: 20px 0;
    }
  `;

  const H2 = styled.h2`
    ${finalHeaderFont}
  `;

  const renderName = (firstName?: string, lastName?: string) => {
    if (!firstName) return null;
    if (!showLastName) return <H2>{firstName}</H2>;
    if (orientation === 'VERTICAL') {
      return (
        <H2>
          {firstName} {lastName}
        </H2>
      );
    }
    return (
      <>
        <H2>{firstName}</H2>
        <H2>{lastName}</H2>
      </>
    );
  };

  return (
    <>
      {renderAbsoluteAssets(namesComponent?.absolute_assets)}
      <Container className={className}>
        <ContentContainer>
          <OwnerName>{renderName(ownerFirstName, ownerLastName)}</OwnerName>
          <And>{articleText}</And>
          <PartnerName>{renderName(partnerFirstName, partnerLastName)}</PartnerName>
        </ContentContainer>
      </Container>
    </>
  );
};

export default NamesModule;
